import React, {
  useState, useMemo, useCallback, useRef, useEffect,
} from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Select,
  MenuItem,
  Skeleton,
  Box,
  CircularProgress,
  Tooltip,
  TableSortLabel,
  Checkbox,
  FormControlLabel,
  IconButton,
  alpha,
  useTheme,
  Dialog,
  DialogContent,
} from '@mui/material';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import dayjs from 'dayjs';
import {
  STATUS_LABELS,
  STATUS_ICONS,
  STATUS_OPTIONS,
  getStatusChipSx,
} from '../../constants/status';
import { TYPE_LABELS } from '../../constants/type';
import { formatDate, formatValue } from '../../utils/format';
import api from '../../../../utils/functions/api';
import ProjectPopup from '../../../Project/ProjectPopup';
import BudgetApproveMenu from './BudgetApproveMenu';
import CellEditModal from './CellEditModal';
import TypeMenu from './TypeMenu';

const TABLE_MAX_HEIGHT = 600;

// column count เยอะขึ้นมาก (mirror ตาม spreadsheet template ที่ user ใช้ติดตามงานประมูลอยู่แล้ว)
// เลิกใช้ tableLayout:'fixed' + สัดส่วน % แบบเดิม (คิดไว้แค่ ~10 คอลัมน์ให้พอดีจอไม่ scroll)
// เปลี่ยนเป็น minWidth ต่อคอลัมน์ + ให้ TableContainer scroll แนวนอนได้แทน
//
// sortKey: ชื่อ field ที่ backend ยอมรับ (ดู SORTABLE_FIELDS ใน opportunity.controller.js) —
// ไม่ใส่ = คอลัมน์นั้นคลิกหัวตารางเพื่อ sort ไม่ได้ (# เป็นเลขลำดับแถวไม่ใช่ค่าจริง, Owner
// ชื่อไม่ได้เก็บในตารางนี้ [loose reference ไป Mongo Employee เก็บแค่ owner_id], วันที่นัด
// เป็น array หลายค่าต่อแถว sort เป็นคอลัมน์เดียวไม่ได้) เดือน/ปี/เหลืออีกกี่วัน เป็นค่าที่
// คำนวณจากฟิลด์อื่น (date_start/quotation_due_date) เลย sort โดยอิงฟิลด์ต้นทางแทน
//
// ตัด Code/Number ออก (ผู้ใช้ไม่ได้ใช้แยกจาก Document No. ในหน้า list) และรวม Month+Year
// เดิมเป็นคอลัมน์เดียว "เดือน/ปี" — เปลี่ยนแหล่งข้อมูลจาก number (yymmxxxx เดิม ตอนนี้ไม่มี
// เดือนในเลขนี้แล้ว ดู opportunity.repository.js) มาเป็น date_start (วันที่สร้างโครงการจริง)
// group: ใช้จัดสีหัวตาราง (ดู GROUP_COLORS/GROUP_HEADER_BG) — ไม่ใส่ = ตกกลุ่ม 'general' (สีเทา)
// sticky: 6 คอลัมน์แรก (#, Document No., วันที่เสนอราคา, วันที่สร้าง, โปรเจค, ลูกค้า) ติดชิดซ้าย
// ตอนเปิด checkbox "ล็อกคอลัมน์" — ต้องอยู่เรียงกัน 6 อันแรกของอาเรย์นี้เท่านั้น เพราะ stickySx(0-5)
// ในแถวข้อมูลด้านล่าง hardcode ตามตำแหน่งไว้
//
// "อีกกี่วันเสนอราคา" เดิมเป็นคอลัมน์แยก ถอดออกแล้ว "ผู้รับผิดชอบ" เดิมเป็นคอลัมน์แยก ถอดออกแล้วเช่นกัน
// — ย้ายกลับไปเป็นบรรทัดที่ 2 ใต้ชื่อโครงการในเซลล์ "โปรเจค" เหมือนก่อนหน้านี้
const COLUMNS = [
  { label: '#', minWidth: 40, sticky: true },
  {
    label: 'Document No.',
    minWidth: 120,
    sortKey: 'opportunity_number',
    sticky: true,
  },
  {
    label: 'วันที่เสนอราคา',
    minWidth: 110,
    sortKey: 'quotation_due_date',
    sticky: true,
    group: 'date',
  },
  {
    label: 'วันที่สร้าง',
    minWidth: 100,
    sortKey: 'date_start',
    sticky: true,
    group: 'date',
  },
  { label: 'โปรเจค', minWidth: 240, sortKey: 'project_name', sticky: true },
  {
    label: 'ลูกค้า',
    minWidth: 220,
    sortKey: 'contact_name_snapshot',
    sticky: true,
  },
  { label: 'สถานะ', minWidth: 190, sortKey: 'status' },
  { label: 'Type', minWidth: 130, sortKey: 'type' },
  { label: 'รายละเอียด', minWidth: 240, sortKey: 'detail' },
  { label: 'วันที่นัด', minWidth: 110, group: 'date' },
  {
    label: 'ราคาที่เสนอ',
    minWidth: 120,
    align: 'right',
    sortKey: 'value',
    group: 'money',
  },
  {
    label: 'Budget',
    minWidth: 120,
    align: 'right',
    sortKey: 'budget',
    group: 'money',
  },
  {
    label: 'Budget (อนุมัติ)',
    minWidth: 130,
    sortKey: 'budget_approve',
    group: 'money',
  },
  { label: 'Note', minWidth: 180, sortKey: 'note', group: 'action' },
  // วันที่ปิดงาน/ผู้สร้าง ย้ายมาไว้ท้ายๆ ต่อจาก Note ตามที่ user ขอ — ดูตำแหน่งเดิมใน git
  // history ถ้าต้องย้ายกลับ (คอลัมน์ "เดือน/ปี" เดิมเคยอยู่ก่อนหน้านี้ ถอดออกไปแล้วตามที่ user ขอ)
  // localSortKey: field ที่ต้อง resolve เป็นชื่อคนก่อนค่อย sort (created_by อ้าง User ใน Mongo
  // ผ่าน uid) — คนละฐานข้อมูลกับ Opportunity (Postgres) เลย sort จริงที่ backend (ORDER BY ตาม
  // field id ดิบๆ) ไม่มีความหมาย (ไม่ได้เรียงตามตัวอักษรชื่อ) ใช้ sort ฝั่ง client จากชื่อที่
  // resolve ไว้แล้วแทน (ดู resolveEmployeeName/localSort ด้านล่าง) — เรียงได้แค่ในชุดแถวที่โหลดมา
  // แล้วเท่านั้น ไม่ใช่ทั้ง dataset เหมือนคอลัมน์อื่นที่มี sortKey จริง
  { label: 'วันที่ปิดงาน', minWidth: 100, sortKey: 'date_end', group: 'date' },
  { label: 'ผู้สร้าง', minWidth: 130, group: 'employee', localSortKey: 'created_by' },
  { label: 'จัดการ', minWidth: 90, group: 'action' },
];

// สีเน้นต่อกลุ่ม (เส้นใต้หัวตาราง) — คอลัมน์ที่ไม่ได้ระบุ group ใน COLUMNS ถือเป็น 'general'
// (ดูตอนใช้งานด้านล่าง: col.group || 'general')
const GROUP_COLORS = {
  general: '#64748b', // slate
  date: '#3b82f6', // blue
  money: '#10b981', // emerald
  employee: '#8b5cf6', // violet
  action: '#06b6d4', // cyan — Note/จัดการ แยกจากม่วงของ employee ตั้งใจ
};
// พื้นหลังหัวตาราง — ใช้สีทึบแสงจริง (ไม่ใช่ alpha()) เพราะหัวตาราง sticky ทั้งแนวตั้ง (stickyHeader)
// และแนวนอน (เมื่อล็อกคอลัมน์) สีโปร่งใสจะเห็นเนื้อหาที่เลื่อนผ่านทะลุออกมา (เหตุผลเดียวกับ
// stickyBg ของแถวข้อมูลด้านล่าง)
const GROUP_HEADER_BG = {
  general: { light: '#f8fafc', dark: '#1e293b' },
  date: { light: '#eff6ff', dark: '#1c2942' },
  money: { light: '#ecfdf5', dark: '#132a20' },
  employee: { light: '#f5f3ff', dark: '#241f3d' },
  action: { light: '#ecfeff', dark: '#083344' },
};

const cellDivider = (idx) =>
  idx < COLUMNS.length - 1
    ? { borderRight: '1px solid', borderColor: 'divider' }
    : {};

// ไอคอนดินสอจางๆ มุมขวาบนของ cell ที่แก้ไขได้ (คลิกขวา) — ซ่อนอยู่เฉยๆ โผล่ตอน hover
// ทั้งแถว (คุมด้วย "&:hover .op-edit-icon" บน TableRow ไม่ใช่ hover เฉพาะ cell)
function EditHint() {
  return (
    <EditOutlinedIcon
      className="op-edit-icon"
      sx={{
        position: 'absolute',
        top: 3,
        right: 3,
        fontSize: 13,
        color: 'text.disabled',
        opacity: 0,
        transition: 'opacity 0.15s',
        pointerEvents: 'none',
      }}
    />
  );
}

// นับถอยหลังเฉพาะสถานะที่ยังต้องติดตามเสนอราคาอยู่จริง — รอผลการดำเนินการ (ON_HOLD) ขึ้นไปนับ
// ว่ายื่นราคาไปแล้วรอผล ไม่ต้องนับถอยหลังต่อเหมือนจบดีลแล้ว (WON/LOST/CANCELLED)
const COUNTDOWN_STATUSES = ['WAITING', 'IN_PROGRESS'];

// เหลืออีกกี่วันถึงวันเสนอราคา — เทียบวันปัจจุบันกับ quotation_due_date ของแต่ละแถว
// null = ไม่ต้องนับ (ไม่ใช่สถานะที่ยังนับถอยหลัง หรือยังไม่ได้ตั้งวันเสนอราคา) แสดงเป็น "-"
// export ไว้ให้ OpportunityStatsBar.js เรียกใช้สูตรเดียวกัน กันตัวเลข "ใกล้ครบกำหนด" เพี้ยนจากตาราง
export function daysUntilQuotationDue(row) {
  if (!COUNTDOWN_STATUSES.includes(row.status) || !row.quotation_due_date)
    return null;
  return dayjs(row.quotation_due_date)
    .startOf('day')
    .diff(dayjs().startOf('day'), 'day');
}

function escapeRegExp(str) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// ไฮไลต์คำค้นหา (q) ที่เจอในข้อความ — ใช้กับคอลัมน์ที่ backend ค้นหาจริง (Document No./โปรเจค/
// ผู้รับผิดชอบ/ลูกค้า/รายละเอียด ดู q ใน opportunity.repository.js) ไม่ได้แปลว่าคอลัมน์ไหนไม่มี
// HighlightText แปลว่า backend ไม่ได้ค้นในฟิลด์นั้น แค่ไม่ได้ไฮไลต์เฉยๆ — เจอหลายจุดในข้อความ
// เดียวกันไฮไลต์ให้หมดทุกจุด ไม่ใช่แค่จุดแรก
function HighlightText({ text, query }) {
  if (!query || text == null) return text;
  const str = String(text);
  const parts = str.split(new RegExp(`(${escapeRegExp(query)})`, 'gi'));
  if (parts.length === 1) return str;
  const q = query.toLowerCase();
  return parts.map((part, i) => (
    part.toLowerCase() === q
      ? (
        // eslint-disable-next-line react/no-array-index-key
        <Box key={i} component="mark" sx={{ bgcolor: 'warning.light', color: 'inherit', borderRadius: 0.5 }}>
          {part}
        </Box>
      )
      : part
  ));
}

// Tooltip ที่โชว์เฉพาะตอนข้อความถูก ellipsis ตัดจริง (scrollWidth > clientWidth ของ element ลูก) —
// ไม่โชว์เปล่าๆ ตอนข้อความสั้นพอแสดงครบอยู่แล้วในเซลล์ (ต่างจาก Tooltip ธรรมดาที่โชว์ทุกครั้งที่มีค่า
// ไม่ว่าจะถูกตัดจริงหรือไม่) ใช้ ResizeObserver แทน useEffect ครั้งเดียวตอน mount เพราะคอลัมน์ในตาราง
// นี้ลากปรับความกว้างเองได้ (ดู startResize ด้านล่าง) ต้องคำนวณ truncated ใหม่ทุกครั้งที่ความกว้าง
// จริงของเซลล์เปลี่ยน ไม่ใช่แค่ตอน mount ครั้งแรก — children ต้องเป็น element เดียวที่ forward ref
// ได้ (เช่น Typography) และมี noWrap/overflow:hidden อยู่แล้วเพื่อให้ scrollWidth ต่างจาก clientWidth
// จริงตอนล้น
function TruncateTooltip({ title, children }) {
  const ref = useRef(null);
  const [truncated, setTruncated] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const check = () => setTruncated(el.scrollWidth > el.clientWidth + 1);
    check();
    const observer = new ResizeObserver(check);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <Tooltip title={title || ''} disableHoverListener={!truncated || !title}>
      {React.cloneElement(children, { ref })}
    </Tooltip>
  );
}

// infinite scroll: onScroll เช็คระยะจากขอบล่างใน useOpportunities.js#handleScroll —
// ต้องมี maxHeight + overflowY ตรงนี้ให้ TableContainer ถึงจะเกิด scroll ให้ดักจับได้
export function OpportunitiesTable({
  rows,
  loading,
  loadingMore,
  hasMore,
  q,
  onRowClick,
  onFilterByContact,
  onFilterByOwner,
  activeContactId,
  activeOwnerId,
  onStatusChange,
  onEditRow,
  onDeleteRow,
  onSaved,
  scrollRef,
  onScroll,
  ownerNameById = {},
  creatorNameByUserId = {},
  sortBy,
  sortOrder,
  onSortChange,
}) {
  // คลิกขวาที่ cell แล้วเด้ง modal แก้ค่าฟิลด์นั้นตรงๆ (ดู CellEditModal.js) — เก็บ
  // {row, fieldKey} ไว้ที่นี่ ไม่แตะ rows array ตรงๆ กันข้อมูลค้าง ใช้ onSaved (refetch จาก
  // parent) รีเฟรชตารางหลังบันทึกสำเร็จเสมอ
  const [editTarget, setEditTarget] = useState(null);
  const openEdit = (e, row, fieldKey) => {
    e.preventDefault();
    setEditTarget({ row, fieldKey });
  };
  const closeEdit = () => setEditTarget(null);

  // budget_approve เป็น boolean ล้วนๆ — คลิกขวาเด้ง popover เลือกเลย ไม่ผ่าน editTarget/
  // CellEditModal ข้างบน (ดู BudgetApproveMenu.js)
  const [budgetApproveMenu, setBudgetApproveMenu] = useState(null);
  const openBudgetApproveMenu = (e, row) => {
    e.preventDefault();
    setBudgetApproveMenu({ row, mouseX: e.clientX, mouseY: e.clientY });
  };
  const closeBudgetApproveMenu = () => setBudgetApproveMenu(null);

  // Type เลือกจาก popover เลือกเลยเหมือนกัน ไม่ผ่าน editTarget/CellEditModal (ดู TypeMenu.js) —
  // ยกเว้นกรณี "Other" ที่ยังต้องพิมพ์ระบุเพิ่ม แต่ทำในตัว popover เดียวกัน ไม่เด้ง modal แยก
  const [typeMenu, setTypeMenu] = useState(null);
  const openTypeMenu = (e, row) => {
    e.preventDefault();
    setTypeMenu({ row, mouseX: e.clientX, mouseY: e.clientY });
  };
  const closeTypeMenu = () => setTypeMenu(null);

  const theme = useTheme();

  // โครงการที่ถูกสร้างจาก opportunity แต่ละใบ (ถ้ามี) — opportunity เก็บ project_id ไว้เองอยู่แล้ว
  // (เขียนโดย useCreateProject.js ทันทีหลังกด "สร้างโครงการ" สำเร็จ ผ่าน MarketingAction.js:
  // linkOpportunityProject) เหลือแค่ resolve project_id → project_number แบบ batch ครั้งเดียวต่อหน้า
  // (ไม่ query ทีละแถว) ไม่ต้องถามว่า "opportunity ใบไหนมีโครงการบ้าง" อีกแล้วเพราะรู้จากข้อมูล
  // opportunity เองตรงๆ
  const [projectByOpportunityId, setProjectByOpportunityId] = useState({});
  useEffect(() => {
    const linked = rows.filter((r) => r.project_id);
    if (!linked.length) {
      setProjectByOpportunityId({});
      return undefined;
    }
    const projectIds = [...new Set(linked.map((r) => r.project_id))];
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(
          `${process.env.REACT_APP_API_URL}/project?ids=${projectIds.join(',')}&lite=true&size=9999`,
        );
        if (cancelled) return;
        const projectById = {};
        (data?.rows || []).forEach((p) => {
          projectById[p._id] = p;
        });
        const map = {};
        linked.forEach((r) => {
          const p = projectById[r.project_id];
          if (p) map[r.id] = p;
        });
        setProjectByOpportunityId(map);
      } catch (error) {
        if (!cancelled) setProjectByOpportunityId({});
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [rows]);
  const [popupProjectId, setPopupProjectId] = useState(null);

  // sort กลุ่ม employee ทำฝั่ง client เท่านั้น (ดูเหตุผลใน comment ของ localSortKey ใน COLUMNS
  // ด้านบน) — วนสถานะ 3 คลิกเหมือนคอลัมน์อื่น: asc → desc → กลับ default (localSort.key = null,
  // ไม่มีคอลัมน์ไหน active) คนละ state กับ sortBy/sortOrder ที่เป็น server sort เพราะไม่ได้ยิง
  // fetch ใหม่ แค่เรียง rows ที่โหลดมาแล้วในเบราว์เซอร์
  const [localSort, setLocalSort] = useState({ key: null, order: 'asc' });
  const handleLocalSortClick = (key) => {
    setLocalSort((prev) => {
      if (prev.key !== key) return { key, order: 'asc' };
      if (prev.order === 'asc') return { key, order: 'desc' };
      return { key: null, order: 'asc' };
    });
  };
  // created_by เก็บ User._id (resolve ผ่าน creatorNameByUserId) ส่วน owner_id/ordered_by
  // เก็บ Employee._id ตรงๆ (resolve ผ่าน ownerNameById) — ไม่มีชื่อ/ไม่มี id = ถือว่า
  // ว่าง ดันไปท้ายสุดเสมอไม่ว่า asc/desc (เหมือน nulls-last ฝั่ง backend ของกลุ่มวันที่/ราคา)
  const resolveEmployeeName = (row, key) => {
    if (key === 'created_by') {
      return row.created_by ? (creatorNameByUserId[row.created_by] || '') : '';
    }
    const id = row[key];
    return id ? (ownerNameById[id] || '') : '';
  };
  const displayRows = useMemo(() => {
    if (!localSort.key) return rows;
    return [...rows].sort((a, b) => {
      const nameA = resolveEmployeeName(a, localSort.key);
      const nameB = resolveEmployeeName(b, localSort.key);
      if (!nameA && !nameB) return 0;
      if (!nameA) return 1;
      if (!nameB) return -1;
      return localSort.order === 'asc'
        ? nameA.localeCompare(nameB, 'th')
        : nameB.localeCompare(nameA, 'th');
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, localSort, ownerNameById, creatorNameByUserId]);
  // ล็อก 6 คอลัมน์แรก (#, Document No., วันที่เสนอราคา, วันที่สร้าง, โปรเจค, ลูกค้า) ให้ติดชิดซ้าย
  // ตอน scroll แนวนอน — ปิดเป็นค่าเริ่มต้น (ต่างจากเวอร์ชันก่อนที่ล็อกตลอด) เปิดได้ด้วย checkbox
  // ด้านบนตาราง
  const [columnsLocked, setColumnsLocked] = useState(false);

  // ความกว้าง default ต่อคอลัมน์ = col.minWidth ตรงๆ (ตัดการคำนวณ auto จากเนื้อหาจริงออกแล้ว
  // ตามที่ตกลงกัน) — manualWidths คือคอลัมน์ที่ user ลากปรับเองแล้ว (เก็บเฉพาะ index ที่ถูกลากจริง
  // sparse object) ค่าที่ลากเองชนะ minWidth เสมอ ไม่ persist ข้าม refresh/localStorage เหมือน
  // checkbox "ล็อกคอลัมน์" ด้านบน
  const [manualWidths, setManualWidths] = useState({});
  const colWidths = useMemo(
    () => COLUMNS.map((col, idx) => manualWidths[idx] ?? col.minWidth),
    [manualWidths],
  );
  // resizeRef เก็บ state ระหว่างลาก (ไม่ผ่าน React state กันการ re-render รัวๆ ทุก pixel ที่ขยับเมาส์)
  const resizeRef = useRef(null);

  const onResizeMove = useCallback((e) => {
    if (!resizeRef.current) return;
    const { idx, startX, startWidth } = resizeRef.current;
    const nextWidth = Math.max(
      COLUMNS[idx].minWidth,
      startWidth + (e.clientX - startX),
    );
    setManualWidths((prev) =>
      prev[idx] === nextWidth ? prev : { ...prev, [idx]: nextWidth },
    );
  }, []);
  const onResizeEnd = useCallback(() => {
    resizeRef.current = null;
    window.removeEventListener('mousemove', onResizeMove);
    window.removeEventListener('mouseup', onResizeEnd);
  }, [onResizeMove]);
  const startResize = (idx) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    resizeRef.current = { idx, startX: e.clientX, startWidth: colWidths[idx] };
    window.addEventListener('mousemove', onResizeMove);
    window.addEventListener('mouseup', onResizeEnd);
  };

  // ลากพื้นที่ตาราง (คลิกค้าง+ลากเมาส์) เพื่อเลื่อน scroll ซ้าย/ขวา/บน/ล่าง แบบ "มือกำ" — panRef
  // เก็บ state ระหว่างลากแบบเดียวกับ resizeRef (ไม่ผ่าน React state) ข้ามจุดเริ่มลากถ้าเป็นปุ่ม/
  // input/select ฯลฯ กันแย่ง event จากคอนโทรลปกติ (สถานะ dropdown, ปุ่มแก้ไข/ลบ, sort header)
  // และไม่เริ่มลากจริงจนกว่าเมาส์ขยับเกิน threshold กันคลิกเฉยๆ กลายเป็นลากโดยไม่ตั้งใจ
  const panRef = useRef(null);
  const onPanMouseMove = useCallback(
    (e) => {
      const p = panRef.current;
      const container = scrollRef.current;
      if (!p || !container) return;
      const dx = e.clientX - p.startX;
      const dy = e.clientY - p.startY;
      if (!p.moved) {
        if (Math.abs(dx) < 4 && Math.abs(dy) < 4) return;
        p.moved = true;
        document.body.style.cursor = 'grabbing';
        document.body.style.userSelect = 'none';
      }
      container.scrollLeft = p.scrollLeft - dx;
      container.scrollTop = p.scrollTop - dy;
    },
    [scrollRef],
  );
  const onPanMouseUp = useCallback(() => {
    window.removeEventListener('mousemove', onPanMouseMove);
    window.removeEventListener('mouseup', onPanMouseUp);
    if (panRef.current?.moved) {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      // กันคลิกที่ปลายทางลาก (เช่น sort header) โดนสั่งทำงานหลังจากที่จริงๆ แค่ลาก scroll
      const suppressClick = (ev) => ev.stopPropagation();
      window.addEventListener('click', suppressClick, {
        capture: true,
        once: true,
      });
    }
    panRef.current = null;
  }, [onPanMouseMove]);
  const onPanMouseDown = (e) => {
    if (e.button !== 0) return;
    if (
      e.target.closest('button, a, input, textarea, select, [role="button"]')
    ) {
      return;
    }
    const container = scrollRef.current;
    if (!container) return;
    panRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      scrollLeft: container.scrollLeft,
      scrollTop: container.scrollTop,
      moved: false,
    };
    window.addEventListener('mousemove', onPanMouseMove);
    window.addEventListener('mouseup', onPanMouseUp);
  };

  // ระยะ left สะสมของแต่ละคอลัมน์ (จาก colWidths จริง ไม่ใช่ minWidth ตายตัวอีกต่อไป — ต้อง
  // คำนวณใหม่ทุกครั้งที่ resize เปลี่ยนความกว้าง ไม่งั้น sticky columns (checkbox "ล็อกคอลัมน์")
  // จะไปวางตำแหน่งผิดตามความกว้างเก่า) ใช้ปักหมุด position:sticky ให้ 6 คอลัมน์แรกติดชิดซ้าย
  const stickyLeft = useMemo(() => {
    const arr = [];
    let left = 0;
    colWidths.forEach((w) => {
      arr.push(left);
      left += w;
    });
    return arr;
  }, [colWidths]);
  const totalWidth = useMemo(() => colWidths.reduce((sum, w) => sum + w, 0), [
    colWidths,
  ]);

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          px: 1.5,
          py: 0.75,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={columnsLocked}
              onChange={(e) => setColumnsLocked(e.target.checked)}
            />
          }
          label={
            <Typography
              variant="caption"
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
            >
              <LockOutlinedIcon sx={{ fontSize: 14 }} />
              ล็อกคอลัมน์ (#, Document No., วันที่เสนอราคา, วันที่สร้าง, โปรเจค,
              ลูกค้า)
            </Typography>
          }
        />
      </Box>
      <TableContainer
        ref={scrollRef}
        onScroll={onScroll}
        onMouseDown={onPanMouseDown}
        sx={{ maxHeight: TABLE_MAX_HEIGHT, overflow: 'auto', cursor: 'grab' }}
      >
        <Table
          size="small"
          stickyHeader
          sx={{
            minWidth: totalWidth,
            // tableLayout:fixed บังคับให้แต่ละคอลัมน์กว้างตามที่กำหนดไว้เป๊ะๆ (ไม่ขยายตาม
            // เนื้อหาที่ยาวเกิน colWidths ปัจจุบันแบบ auto layout เดิม) — จำเป็นสำหรับทั้ง sticky
            // columns (checkbox "ล็อกคอลัมน์") และการลากปรับความกว้าง เพราะ stickyLeft คำนวณ
            // left ล่วงหน้าจาก colWidths ถ้าคอลัมน์ขยายเองตามเนื้อหาได้ offset จะเพี้ยนทันที
            tableLayout: 'fixed',
          }}
        >
          <TableHead>
            <TableRow>
              {COLUMNS.map((col, idx) => (
                <TableCell
                  key={col.label}
                  // คลิกได้ทั้ง cell เพื่อ sort ไม่ใช่แค่ตัวหนังสือ label เหมือนเดิม — ตัว
                  // TableSortLabel ด้านล่างเลยตัด onClick ของมันเองออก เหลือแค่ที่นี่ที่เดียว
                  // กันคลิกซ้อนสองรอบ (คลิกที่ label จะ bubble ขึ้นมาโดน onClick นี้อยู่แล้ว) —
                  // sortKey = server sort ปกติ, localSortKey = sort ฝั่ง client (กลุ่ม employee)
                  onClick={
                    col.sortKey
                      ? () => onSortChange(col.sortKey)
                      : col.localSortKey
                        ? () => handleLocalSortClick(col.localSortKey)
                        : undefined
                  }
                  sx={{
                    fontWeight: 700,
                    width: colWidths[idx],
                    minWidth: colWidths[idx],
                    textAlign: col.align,
                    color: 'text.secondary',
                    fontSize: '0.75rem',
                    whiteSpace: 'nowrap',
                    py: 1.5,
                    cursor: col.sortKey || col.localSortKey ? 'pointer' : 'default',
                    bgcolor: (theme) =>
                      GROUP_HEADER_BG[col.group || 'general'][
                        theme.palette.mode === 'dark' ? 'dark' : 'light'
                      ],
                    borderBottom: `3px solid ${
                      GROUP_COLORS[col.group || 'general']
                    }`,
                    ...cellDivider(idx),
                    ...(columnsLocked && col.sticky
                      ? { left: stickyLeft[idx], zIndex: 3 }
                      : {}),
                  }}
                >
                  {col.sortKey || col.localSortKey ? (
                    <TableSortLabel
                      active={
                        col.sortKey
                          ? sortBy === col.sortKey
                          : localSort.key === col.localSortKey
                      }
                      direction={
                        col.sortKey
                          ? (sortBy === col.sortKey ? sortOrder : 'asc')
                          : (localSort.key === col.localSortKey ? localSort.order : 'asc')
                      }
                      // ยังไม่ active: ไอคอน UnfoldMore (ลูกศรคู่ขึ้น-ลง) บอกว่า sort ได้ เฉยๆ
                      // ไม่ชี้ทิศทางใดทิศทางหนึ่ง — พอ active ค่อยสลับเป็นลูกศรเดี่ยวปกติของ MUI
                      // (ขึ้น=asc/ลง=desc) ตามที่ user ยืนยันว่าโอเคแล้ว ไม่ต้องเปลี่ยน
                      IconComponent={
                        (col.sortKey ? sortBy === col.sortKey : localSort.key === col.localSortKey)
                          ? undefined
                          : UnfoldMoreIcon
                      }
                      sx={{
                        '& .MuiTableSortLabel-icon': {
                          opacity: (col.sortKey
                            ? sortBy === col.sortKey
                            : localSort.key === col.localSortKey)
                            ? 1 : 0.4,
                        },
                      }}
                    >
                      {col.label}
                    </TableSortLabel>
                  ) : (
                    col.label
                  )}
                  {/* ลากขอบขวาของหัวคอลัมน์เพื่อปรับความกว้าง — วางเป็นลูกของ TableCell ตรงๆ
                      (ไม่ต้องเติม position:relative ให้ TableCell เอง เพราะ stickyHeader
                      ของ MUI ใส่ position:sticky มาให้ทุก cell หัวตารางอยู่แล้ว ซึ่งก็เป็น
                      containing block ให้ลูก position:absolute ได้เหมือนกัน — ถ้าเซ็ต
                      position:relative ทับเองจะไปลบ position:sticky ทิ้ง ตารางเลื่อนแนวตั้ง
                      ตามไม่ได้อีกต่อไป) */}
                  <Box
                    onMouseDown={startResize(idx)}
                    onClick={(e) => e.stopPropagation()}
                    sx={{
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      bottom: 0,
                      width: 6,
                      cursor: 'col-resize',
                      zIndex: 10,
                      '&:hover': { bgcolor: 'primary.main', opacity: 0.5 },
                      '&:active': { bgcolor: 'primary.main', opacity: 0.7 },
                    }}
                  />
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  {COLUMNS.map((col, j) => (
                    <TableCell key={col.label} sx={cellDivider(j)}>
                      <Skeleton variant="text" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={COLUMNS.length}
                  sx={{
                    textAlign: 'center',
                    py: 8,
                    color: 'text.secondary',
                    border: 0,
                  }}
                >
                  <InboxOutlinedIcon
                    sx={{ fontSize: 40, opacity: 0.4, mb: 1 }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    ไม่พบโอกาสทางธุรกิจ
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              displayRows.map((row, idx) => {
                // แถบสีสลับ (zebra) — ใช้สี alpha บางๆ กับพื้นหลังแถวปกติได้ (ไม่มีอะไรเลื่อนอยู่
                // ข้างใต้) แต่ cell ที่ sticky ต้องใช้สี "ทึบแสงจริง" แยกต่างหาก (stickyBg) เพราะ
                // สี alpha 2.5% แทบโปร่งใส เห็นตัวหนังสือ/เส้นขอบของคอลัมน์อื่นที่เลื่อนผ่านมา
                // ทะลุออกมาให้เห็นด้านหลัง (ดู checkbox "ล็อกคอลัมน์" ด้านบนตาราง)
                const rowBg =
                  idx % 2 === 1
                    ? alpha(theme.palette.primary.main, 0.025)
                    : theme.palette.background.paper;
                const stickyBg =
                  idx % 2 === 1
                    ? theme.palette.grey[50]
                    : theme.palette.background.paper;
                // position ต้องมาจากตรงนี้เท่านั้น (ไม่ใช่เขียน position:'relative' แยกต่างหาก
                // ทับท้าย sx object อีกที) ไม่งั้นตอนล็อกคอลัมน์ position:'sticky' จะโดนทับ
                // กลับเป็น 'relative' แต่ left/zIndex ยังติดอยู่ — กลายเป็น relative+left ที่
                // เลื่อนเนื้อหาออกนอกกรอบ cell ของตัวเอง (นี่คือสาเหตุตัวจริงที่ตารางเพี้ยน)
                const stickySx = (i) =>
                  columnsLocked
                    ? {
                        position: 'sticky',
                        left: stickyLeft[i],
                        zIndex: 1,
                        bgcolor: stickyBg,
                      }
                    : { position: 'relative' };
                return (
                  <TableRow
                    key={row.id}
                    hover
                    sx={{
                      bgcolor: rowBg,
                      '&:hover .op-edit-icon': { opacity: 1 },
                    }}
                  >
                    <TableCell sx={{ ...cellDivider(0), ...stickySx(0) }}>
                      {idx + 1}
                    </TableCell>
                    <TableCell
                      onContextMenu={(e) => openEdit(e, row, 'documentNo')}
                      sx={{
                        ...cellDivider(1),
                        ...stickySx(1),
                        cursor: 'context-menu',
                      }}
                    >
                      <TruncateTooltip title={row.opportunity_number}>
                        <Typography variant="body2" fontWeight={600} noWrap>
                          <HighlightText text={row.opportunity_number} query={q} />
                        </Typography>
                      </TruncateTooltip>
                      {projectByOpportunityId[row.id] && (
                        <Tooltip title="ดับเบิ้ลคลิกเพื่อเปิดโครงการ">
                          <Typography
                            variant="caption"
                            color="primary"
                            noWrap
                            onDoubleClick={(e) => {
                              e.stopPropagation();
                              setPopupProjectId(projectByOpportunityId[row.id]._id);
                            }}
                            sx={{ display: 'block', cursor: 'pointer', fontWeight: 600 }}
                          >
                            {projectByOpportunityId[row.id].project_number}
                          </Typography>
                        </Tooltip>
                      )}
                      <EditHint />
                    </TableCell>
                    <TableCell
                      onContextMenu={(e) =>
                        openEdit(e, row, 'quotation_due_date')
                      }
                      sx={{
                        ...cellDivider(2),
                        ...stickySx(2),
                        cursor: 'context-menu',
                      }}
                    >
                      {formatDate(row.quotation_due_date)}
                      <EditHint />
                    </TableCell>
                    <TableCell sx={{ ...cellDivider(3), ...stickySx(3) }}>
                      {formatDate(row.date_start)}
                    </TableCell>
                    <TableCell
                      onDoubleClick={() => onRowClick(row)}
                      onContextMenu={(e) => openEdit(e, row, 'project_name')}
                      sx={{
                        ...cellDivider(4),
                        ...stickySx(4),
                        overflow: 'hidden',
                        cursor: 'pointer',
                      }}
                    >
                      <TruncateTooltip title={row.project_name}>
                        <Typography variant="body2" fontWeight={600} noWrap>
                          <HighlightText text={row.project_name} query={q} />
                        </Typography>
                      </TruncateTooltip>
                      {row.ordered_by && (
                        <TruncateTooltip title={`ผู้ประสานงาน: ${ownerNameById[row.ordered_by] || '...'}`}>
                          <Typography
                            variant="caption"
                            noWrap
                            sx={{
                              display: 'block',
                              color: 'text.secondary',
                            }}
                          >
                            {`ผู้ประสานงาน: ${ownerNameById[row.ordered_by] || '...'}`}
                          </Typography>
                        </TruncateTooltip>
                      )}
                      <TruncateTooltip title={row.owner_id ? (ownerNameById[row.owner_id] || '...') : ''}>
                        <Typography
                          variant="caption"
                          noWrap
                          // คลิกซ้ายที่ชื่อผู้รับผิดชอบ = กรองตารางเหลือแค่โปรเจกต์ของคนนี้ (toggle
                          // เหมือนคลิกลูกค้า) — stopPropagation กันไปโดน double-click เปิด popup ของ
                          // cell แม่ ใส่สี primary + ขีดเส้นใต้ตอน hover ให้รู้ว่ากดแล้ว filter ได้
                          // (ไม่มี owner ให้กด = สีปกติ ไม่ใช่สีคลิกได้) — คลิกขวาไม่ stopPropagation
                          // แล้ว ปล่อยให้เด้ง (bubble) ไปเปิด modal รวม "ชื่อโครงการ/ผู้รับผิดชอบ/
                          // ผู้ประสานงาน" ของ cell แม่แทน (ตามที่ user ขอ ไม่แยก modal ผู้รับผิดชอบ/
                          // ผู้ประสานงานออกมาเดี่ยวๆ แล้ว)
                          onClick={(e) => {
                            e.stopPropagation();
                            onFilterByOwner?.(row);
                          }}
                          sx={{
                            display: 'block',
                            cursor: 'pointer',
                            color: row.owner_id
                              ? (activeOwnerId === row.owner_id ? 'primary.dark' : 'primary.main')
                              : 'text.secondary',
                            fontWeight: activeOwnerId === row.owner_id && row.owner_id ? 700 : 500,
                            textDecoration: activeOwnerId === row.owner_id && row.owner_id ? 'underline' : 'none',
                            '&:hover': row.owner_id ? { textDecoration: 'underline' } : undefined,
                          }}
                        >
                          {row.owner_id ? (
                            <HighlightText text={ownerNameById[row.owner_id] || '...'} query={q} />
                          ) : '-'}
                        </Typography>
                      </TruncateTooltip>
                      <EditHint />
                    </TableCell>
                    <TableCell
                      // คลิกซ้าย 1 ครั้งที่ชื่อลูกค้า = กรองตารางเหลือแค่โปรเจกต์ของลูกค้าคนนี้
                      // (ตั้งค่า contactFilter ผ่าน onFilterByContact จาก parent) — คลิกขวายังคง
                      // เป็น quick-edit เหมือนคอลัมน์อื่นทุกอันเหมือนเดิม (ไม่ใช้คลิกขวาสำหรับ
                      // filter เพราะคลิกขวา = แก้ไขข้อมูลตาม convention ของตารางนี้)
                      onClick={() => onFilterByContact?.(row)}
                      onContextMenu={(e) => openEdit(e, row, 'contact')}
                      sx={{
                        ...cellDivider(5),
                        ...stickySx(5),
                        cursor: 'pointer',
                      }}
                    >
                      <TruncateTooltip title={row.contact_name_snapshot}>
                        <Typography
                          variant="body2"
                          noWrap
                          sx={{
                            // สี primary + ขีดเส้นใต้ตอน hover ให้รู้ว่ากดแล้ว filter ได้ เหมือนกับ
                            // ชื่อผู้รับผิดชอบในคอลัมน์ "โปรเจค"
                            color: row.contact_id
                              ? (activeContactId === row.contact_id ? 'primary.dark' : 'primary.main')
                              : 'text.primary',
                            fontWeight: activeContactId === row.contact_id && row.contact_id ? 700 : 400,
                            textDecoration: activeContactId === row.contact_id && row.contact_id ? 'underline' : 'none',
                            '&:hover': row.contact_id ? { textDecoration: 'underline' } : undefined,
                          }}
                        >
                          {row.contact_name_snapshot
                            ? <HighlightText text={row.contact_name_snapshot} query={q} />
                            : '-'}
                        </Typography>
                      </TruncateTooltip>
                      <EditHint />
                    </TableCell>
                    <TableCell
                      sx={{
                        ...cellDivider(6),
                        position: 'relative',
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Select
                        size="small"
                        variant="standard"
                        disableUnderline
                        value={row.status}
                        onChange={(e) => onStatusChange(row, e.target.value)}
                        renderValue={(value) => {
                          const Icon = STATUS_ICONS[value];
                          return (
                            <Chip
                              size="small"
                              label={STATUS_LABELS[value] || value}
                              variant="outlined"
                              icon={
                                Icon ? <Icon fontSize="small" /> : undefined
                              }
                              sx={{ maxWidth: '100%', ...getStatusChipSx(value, 'outlined') }}
                            />
                          );
                        }}
                        sx={{
                          width: '100%',
                          '& .MuiSelect-select': { overflow: 'hidden' },
                        }}
                      >
                        {STATUS_OPTIONS.filter((s) => s !== row.status).map(
                          (s) => {
                            const Icon = STATUS_ICONS[s];
                            return (
                              <MenuItem key={s} value={s}>
                                {Icon && (
                                  <Icon
                                    fontSize="small"
                                    sx={{ mr: 1, color: 'text.secondary' }}
                                  />
                                )}
                                {STATUS_LABELS[s]}
                              </MenuItem>
                            );
                          },
                        )}
                      </Select>
                    </TableCell>
                    <TableCell
                      onContextMenu={(e) => openTypeMenu(e, row)}
                      sx={{
                        ...cellDivider(7),
                        position: 'relative',
                        cursor: 'context-menu',
                      }}
                    >
                      {row.type ? (
                        <Tooltip
                          title={
                            row.type === 'OTHER' ? row.type_other || '' : ''
                          }
                          disableHoverListener={
                            row.type !== 'OTHER' || !row.type_other
                          }
                        >
                          <Chip
                            size="small"
                            label={TYPE_LABELS[row.type] || row.type}
                            variant="outlined"
                          />
                        </Tooltip>
                      ) : (
                        '-'
                      )}
                      <EditHint />
                    </TableCell>
                    <TableCell
                      onContextMenu={(e) => openEdit(e, row, 'detail')}
                      sx={{
                        ...cellDivider(8),
                        position: 'relative',
                        cursor: 'context-menu',
                      }}
                    >
                      <TruncateTooltip title={row.detail}>
                        <Typography variant="body2" noWrap>
                          {row.detail ? <HighlightText text={row.detail} query={q} /> : '-'}
                        </Typography>
                      </TruncateTooltip>
                      <EditHint />
                    </TableCell>
                    <TableCell
                      onContextMenu={(e) => openEdit(e, row, 'appointments')}
                      sx={{
                        ...cellDivider(9),
                        position: 'relative',
                        cursor: 'context-menu',
                      }}
                    >
                      {(row.marketing_appointments || []).length > 0 ? (
                        <TruncateTooltip
                          title={row.marketing_appointments
                            .map((a) =>
                              a.title
                                ? `${dayjs(a.date).format('DD/MM/YY')} — ${
                                    a.title
                                  }`
                                : dayjs(a.date).format('DD/MM/YY'),
                            )
                            .join(', ')}
                        >
                          <Typography variant="body2" noWrap>
                            {row.marketing_appointments
                              .map((a) =>
                                a.title
                                  ? `${dayjs(a.date).format('DD/MM/YY')} (${
                                      a.title
                                    })`
                                  : dayjs(a.date).format('DD/MM/YY'),
                              )
                              .join(', ')}
                          </Typography>
                        </TruncateTooltip>
                      ) : (
                        <Typography variant="body2" noWrap>
                          -
                        </Typography>
                      )}
                      <EditHint />
                    </TableCell>
                    <TableCell
                      onContextMenu={(e) => openEdit(e, row, 'value')}
                      sx={{
                        ...cellDivider(10),
                        position: 'relative',
                        textAlign: 'right',
                        cursor: 'context-menu',
                      }}
                    >
                      {formatValue(row.value)}
                      <EditHint />
                    </TableCell>
                    <TableCell
                      onContextMenu={(e) => openEdit(e, row, 'budget')}
                      sx={{
                        ...cellDivider(11),
                        position: 'relative',
                        textAlign: 'right',
                        cursor: 'context-menu',
                      }}
                    >
                      {formatValue(row.budget)}
                      <EditHint />
                    </TableCell>
                    <TableCell
                      onContextMenu={(e) => openBudgetApproveMenu(e, row)}
                      sx={{
                        ...cellDivider(12),
                        position: 'relative',
                        cursor: 'context-menu',
                      }}
                    >
                      <Chip
                        size="small"
                        label={row.budget_approve ? 'อนุมัติ' : 'ยังไม่อนุมัติ'}
                        color={row.budget_approve ? 'success' : 'default'}
                        variant="outlined"
                      />
                      <EditHint />
                    </TableCell>
                    <TableCell
                      onContextMenu={(e) => openEdit(e, row, 'note')}
                      sx={{
                        ...cellDivider(13),
                        position: 'relative',
                        cursor: 'context-menu',
                      }}
                    >
                      <TruncateTooltip title={row.note}>
                        <Typography variant="body2" noWrap>
                          {row.note || '-'}
                        </Typography>
                      </TruncateTooltip>
                      <EditHint />
                    </TableCell>
                    <TableCell sx={cellDivider(14)}>
                      {formatDate(row.date_end)}
                    </TableCell>
                    <TableCell
                      onContextMenu={(e) => openEdit(e, row, 'created_by')}
                      sx={{
                        ...cellDivider(15),
                        position: 'relative',
                        cursor: 'context-menu',
                      }}
                    >
                      {row.created_by
                        ? creatorNameByUserId[row.created_by] || '...'
                        : '-'}
                      <EditHint />
                    </TableCell>
                    <TableCell
                      sx={{ ...cellDivider(16), textAlign: 'center', p: 0.5 }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          flexWrap: 'nowrap',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 0.25,
                        }}
                      >
                        <Tooltip title="แก้ไข">
                          <IconButton
                            size="small"
                            onClick={() => onEditRow?.(row)}
                          >
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="ลบ">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => onDeleteRow?.(row)}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>

        {loadingMore && (
          // position:sticky + left:50% + translateX(-50%) แทน justifyContent:center ธรรมดา —
          // ธรรมดาจะ centered เทียบกับความกว้างจริงของแถว (ที่กว้างเท่า Table รวม totalWidth)
          // ทำให้จุดกึ่งกลางเลื่อนหลุดจอไปเวลา scroll ซ้าย/ขวา วิธีนี้ centered เทียบกับ viewport
          // ที่มองเห็นจริงของ TableContainer เสมอ ไม่ขยับตาม scrollLeft เลย
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              py: 1.5,
              position: 'sticky',
              left: '50%',
              width: 'fit-content',
              transform: 'translateX(-50%)',
            }}
          >
            <CircularProgress size={20} />
          </Box>
        )}
        {!loading && !hasMore && rows.length > 0 && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: 'block',
              textAlign: 'center',
              py: 1.5,
              position: 'sticky',
              left: '50%',
              width: 'fit-content',
              transform: 'translateX(-50%)',
              whiteSpace: 'nowrap',
            }}
          >
            — สิ้นสุดรายการ ({rows.length} รายการ) —
          </Typography>
        )}
      </TableContainer>

      <CellEditModal
        open={!!editTarget}
        row={editTarget?.row}
        fieldKey={editTarget?.fieldKey}
        onClose={closeEdit}
        onSaved={() => onSaved?.()}
      />

      <BudgetApproveMenu
        target={budgetApproveMenu}
        onClose={closeBudgetApproveMenu}
        onSaved={() => onSaved?.()}
      />

      <TypeMenu
        target={typeMenu}
        onClose={closeTypeMenu}
        onSaved={() => onSaved?.()}
      />

      {/* ดับเบิ้ลคลิกที่ project_number ใต้ Document No. → เปิด P111 project popup ตัวเดียวกับที่
      ใช้ทั่วระบบ (PaymentApproval.js/ExpensesTabToday.js ก็ใช้ pattern เดียวกันนี้) */}
      <Dialog
        open={!!popupProjectId}
        onClose={() => setPopupProjectId(null)}
        maxWidth="xl"
        fullWidth
        sx={{ '& .MuiDialog-container': { alignItems: 'flex-start' } }}
        PaperProps={{
          sx: { mt: '40px', maxHeight: 'calc(100vh - 56px)', minWidth: '50vw' },
        }}
        hideBackdrop
      >
        <DialogContent sx={{ p: 0 }}>
          {popupProjectId && (
            <ProjectPopup
              propId={popupProjectId}
              onClose={() => setPopupProjectId(null)}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default OpportunitiesTable;
